import { useState } from "react";
import { useForm, router } from "@inertiajs/react";
import { UserPlus, Trash2, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select } from "@/components/ui/select";
import FormField from "@/components/forms/FormField";
import SelectField from "@/components/forms/SelectField";
import { DataTable, Th, Td, rowClass } from "@/components/shared/DataTable";
import { useConfirm } from "@/components/shared/ConfirmProvider";
import { pluralize } from "@/lib/format";
import { INVITES_HELD_NOTE } from "@/components/schools/setup/InviteAdminForm";

const ROLE_LABELS = { school_admin: "School admin", teacher: "Teacher" };
const roleOptions = (roles) => roles.map((r) => ({ value: r, label: ROLE_LABELS[r] || r }));

/**
 * The members table that lives on the school's Members page. Pure
 * presentational + behaviour — handles its own row-level actions
 * via the inertia router. Pass `members` and `roles` from the page.
 *
 * `actionsEnabled` controls whether the row actions are wired up. When
 * false, the table is render-only (used by the help centre to show what
 * the page looks like).
 */
export default function MembersTable({ members, roles, actionsEnabled = true }) {
  const admins = members.filter((m) => m.role === "school_admin").length;
  const teachers = members.filter((m) => m.role === "teacher").length;

  return (
    <div className="space-y-3">
      <DataTable>
        <thead>
          <tr>
            <Th>Name</Th>
            <Th>Email</Th>
            <Th>Role</Th>
            <Th srOnly>Actions</Th>
          </tr>
        </thead>
        <tbody>
          {members.map((m) => <MemberRow key={m.id} member={m} roles={roles} actionsEnabled={actionsEnabled} />)}
        </tbody>
      </DataTable>
      <p className="text-xs text-muted-foreground">
        {pluralize(members.length, "member")} · {pluralize(admins, "admin")} · {pluralize(teachers, "teacher")}
      </p>
    </div>
  );
}

function MemberRow({ member, roles, actionsEnabled }) {
  const confirm = useConfirm();
  const [editingName, setEditingName] = useState(false);
  const { data, setData, patch, processing } = useForm({ name: member.name || "" });
  const nameId = `member-name-${member.id}`;

  const onChangeRole = async (newRole) => {
    if (!actionsEnabled || newRole === member.role) return;
    if (newRole === "school_admin") {
      const ok = await confirm({
        title: `Make ${member.name} a school admin?`,
        body: "Admins can do everything: see donor details, connect or disconnect Square, and manage the whole team.",
        confirmLabel: "Make admin",
      });
      if (!ok) return;
    }
    router.patch(member.update_path, { role: newRole }, { preserveScroll: true });
  };

  const onSaveName = (e) => {
    e.preventDefault();
    if (!actionsEnabled) return;
    patch(member.update_path, { preserveScroll: true, onSuccess: () => setEditingName(false) });
  };

  const onRemove = async () => {
    if (!actionsEnabled) return;
    const ok = await confirm({
      title: `Remove ${member.name}?`,
      body: "They lose access to this school right away. Minutes they logged stay.",
      confirmLabel: "Remove",
      destructive: true,
    });
    if (ok) router.delete(member.delete_path, { preserveScroll: true });
  };

  return (
    <tr className={rowClass}>
      <Td>
        {editingName ? (
          <form onSubmit={onSaveName} className="flex flex-wrap items-center gap-2">
            <label htmlFor={nameId} className="sr-only">Name</label>
            <input
              id={nameId}
              value={data.name}
              onChange={(e) => setData("name", e.target.value)}
              className="h-9 w-48 rounded-lg border border-input bg-card px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              autoFocus
            />
            <Button type="submit" size="sm" disabled={processing || !data.name.trim()}>{processing ? "Saving…" : "Save"}</Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => { setData("name", member.name || ""); setEditingName(false); }}>Cancel</Button>
          </form>
        ) : (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-medium text-foreground">{member.name}</span>
            {member.is_self && <span className="text-xs text-muted-foreground">(you)</span>}
            {!member.signed_in && <Badge variant="warning" className="text-[11px]">Hasn't signed in yet</Badge>}
            {actionsEnabled && (
              <button
                type="button"
                onClick={() => setEditingName(true)}
                aria-label={`Rename ${member.name}`}
                title="Rename"
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-secondary/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        )}
      </Td>
      <Td className="text-muted-foreground">{member.email}</Td>
      <Td>
        <label htmlFor={`member-role-${member.id}`} className="sr-only">Role for {member.name}</label>
        <Select
          id={`member-role-${member.id}`}
          size="sm"
          value={member.role}
          onChange={(e) => onChangeRole(e.target.value)}
          disabled={member.is_self || !actionsEnabled}
          options={roleOptions(roles)}
          className="w-40"
        />
      </Td>
      <Td align="right">
        {!member.is_self && (
          <Button type="button" size="sm" variant="ghost" onClick={onRemove} className="hover:text-destructive" aria-label={`Remove ${member.name}`}>
            <Trash2 className="h-4 w-4" /> Remove
          </Button>
        )}
      </Td>
    </tr>
  );
}

/**
 * Inline "invite a member" form. The page toggles it via state; the
 * help centre renders it directly inside a Screenshot.
 */
export function InviteMemberForm({
  createPath = "#",
  roles = ["school_admin", "teacher"],
  onCancel,
  initialName = "",
  initialEmail = "",
  initialRole = "teacher",
  invitesHeld = false,
}) {
  const { data, setData, post, processing, reset, errors } = useForm({
    name: initialName,
    email: initialEmail,
    role: initialRole,
  });

  const submit = (e) => {
    e.preventDefault();
    post(createPath, { onSuccess: () => { reset(); onCancel?.(); } });
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <form onSubmit={submit} className="space-y-4">
        <p className="text-sm font-semibold text-foreground">Invite someone</p>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <FormField name="name" label="Name" placeholder="Ms. Garcia" value={data.name} onChange={(e) => setData("name", e.target.value)} autoFocus error={errors.name} />
          <FormField name="email" label="Email" type="email" required placeholder="garcia@school.ca" value={data.email} onChange={(e) => setData("email", e.target.value)} error={errors.email} />
          <SelectField
            name="role"
            label="Role"
            value={data.role}
            onChange={(e) => setData("role", e.target.value)}
            options={roleOptions(roles)}
            note={data.role === "school_admin"
              ? "Admins can do everything, including seeing donor details and managing Square."
              : "Teachers log reading and hand out prizes for their classrooms. Add them to a classroom afterwards."}
          />
        </div>
        <div className="flex gap-2 pt-1">
          <Button type="submit" size="sm" disabled={processing || !data.email}>
            <UserPlus className="h-4 w-4" />
            {processing ? "Saving…" : invitesHeld ? "Add them" : "Send invitation"}
          </Button>
          {onCancel && <Button type="button" variant="ghost" size="sm" onClick={onCancel}>Cancel</Button>}
        </div>
        <p className="text-xs text-muted-foreground">
          {invitesHeld
            ? `${INVITES_HELD_NOTE} Teachers can be added once your campaign is paid for.`
            : "They'll get an email with a sign-in link. There's no password."}
        </p>
      </form>
    </div>
  );
}
